import _ from "lodash";
import { FieldValues, UseFormSetValue } from "react-hook-form";

import {
  IAreaPriceAdditionDto,
  safeParseNumericStringOrUndefined,
} from "@ryde/shared";

export interface IAreaAdditionSortedEntry {
  item: IAreaPriceAdditionDto;
  formIndex: number;
}

export interface IParsedStartingFromAreaIndex {
  /** the storable positive integer; absent when the input means "unset" or is invalid */
  value?: number;
  isInvalid?: boolean;
}

/**
 * `startingFromAreaIndex` is a 1-based ordinal: `""`/`0` mean unset (and are not stored), and
 * only a positive integer is storable. A fractional or negative input is invalid and must be
 * rejected on save — silently storing it would create a misleading rule (the backend treats
 * such values as unset).
 */
export const parseStartingFromAreaIndexInput = (
  rawValue: unknown,
): IParsedStartingFromAreaIndex => {
  const parsed = safeParseNumericStringOrUndefined(rawValue) || undefined; // 0 = unset

  if (parsed === undefined) {
    return {};
  }

  return Number.isInteger(parsed) && parsed > 0
    ? { value: parsed }
    : { isInvalid: true };
};

/**
 * Sort by price, then by sequence number, for display while keeping the index in the form
 * array (setValue path) — so equally-priced step rules read in ladder order.
 */
export const buildSortedAreaAdditionEntries = (
  raw: IAreaPriceAdditionDto[],
): IAreaAdditionSortedEntry[] =>
  raw
    .map((item, formIndex) => ({ item, formIndex }))
    .sort(
      (a, b) =>
        Number(a.item.price) - Number(b.item.price) ||
        (Number(a.item.startingFromAreaIndex) || 0) -
          (Number(b.item.startingFromAreaIndex) || 0),
    );

export const normalizeAreaAdditionFieldData = (
  watchedList: unknown,
  fieldName: string,
  setValue: UseFormSetValue<FieldValues>,
): IAreaPriceAdditionDto[] => {
  if (watchedList && !_.isArray(watchedList)) {
    /* guard for invalid value coming from backend*/
    setValue(fieldName, []);
    return [];
  }

  return (watchedList || []) as IAreaPriceAdditionDto[];
};
